import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import type { RootState } from './index';

export enum DocumentMode {
  VIEW = 'view',
  COMPARE = 'compare',
  PLAGIARISM = 'plagiarism',
}

export type PlagiarismType = "Coincidences" | "Ai"

interface UIState {
  documentMode: DocumentMode;
  leftVersion: number;
  rightVersion: number;
  hoveredParagraphId: string | null;
  selectedParagraphId: string | null;
  openedParagraphPropertiesId: string;
  /** Ручной масштаб страниц в режиме сравнения (используется при zoomMode = "manual"). */
  zoomLevel: number;
  /**
   * "fit" — масштаб подбирается по ширине панели сравнения и следует за её
   * размером; "manual" — пользователь задал масштаб сам (кнопки, хоткеи).
   */
  zoomMode: "fit" | "manual";
  /** Масштаб «по ширине», который измеряет DocumentViewer. */
  fitZoom: number;
  plagiarismType: PlagiarismType;
  /** Обновить список отчётов антиплагиата (при событии antiplagiat-documents-updated) */
  antiplagiatDocumentsInvalidatedAt: number | null;
}

const initialState: UIState = {
  documentMode: DocumentMode.VIEW,
  leftVersion: 1,
  rightVersion: 2,
  hoveredParagraphId: null,
  selectedParagraphId: null,
  openedParagraphPropertiesId: null,
  zoomLevel: 0.7,
  zoomMode: "fit",
  fitZoom: 0.7,
  plagiarismType: "Coincidences",
  antiplagiatDocumentsInvalidatedAt: null,
};

export const ZOOM_MIN = 0.25;
export const ZOOM_MAX = 3;
export const ZOOM_STEP = 0.1;

const uiSlice = createSlice({
  name: 'ui',
  initialState,
  reducers: {
    setDocumentMode(state, action: PayloadAction<DocumentMode>) {
      state.documentMode = action.payload;
    },
    setLeftVersion(state, action: PayloadAction<number>) {
      state.leftVersion = action.payload;
    },
    setRightVersion(state, action: PayloadAction<number>) {
      state.rightVersion = action.payload;
    },
    setHoveredParagraphId(state, action: PayloadAction<string | null>) {
      state.hoveredParagraphId = action.payload;
    },
    setSelectedParagraphId(state, action: PayloadAction<string | null>) {
      state.selectedParagraphId = action.payload;
    },
    setOpenedParagraphPropertiesId(state, action) {
      state.openedParagraphPropertiesId = action.payload;
    },
    setZoomLevel(state, action: PayloadAction<number>) {
      // Пределы 25–300%, округление до 5%: хвосты вроде 0.7000000001 не копятся.
      const clamped = Math.max(ZOOM_MIN, Math.min(ZOOM_MAX, action.payload));
      state.zoomLevel = Math.round(clamped * 20) / 20;
      state.zoomMode = "manual";
    },
    setZoomFit(state) {
      state.zoomMode = "fit";
    },
    setFitZoom(state, action: PayloadAction<number>) {
      state.fitZoom = action.payload;
    },
    setPlagiarismType(state, action: PayloadAction<PlagiarismType>) {
      state.plagiarismType = action.payload
    },
    setAntiplagiatDocumentsInvalidated(state, action: PayloadAction<number | null>) {
      state.antiplagiatDocumentsInvalidatedAt = action.payload
    },
    clearUIState() {
      return initialState;
    }
  },
});

export const {
  setDocumentMode,
  setLeftVersion,
  setRightVersion,
  setHoveredParagraphId,
  setSelectedParagraphId,
  setOpenedParagraphPropertiesId,
  setZoomLevel,
  setZoomFit,
  setFitZoom,
  clearUIState,
  setPlagiarismType,
  setAntiplagiatDocumentsInvalidated,
} = uiSlice.actions;

export const selectUI = (state: RootState) => state.ui;
export const selectDocumentMode = (state: RootState) => state.ui.documentMode;
/** Масштаб, с которым реально рисуются страницы в режиме сравнения. */
export const selectEffectiveZoom = (state: RootState) =>
  state.ui.zoomMode === "fit" ? state.ui.fitZoom : state.ui.zoomLevel;
export const selectAntiplagiatDocumentsInvalidatedAt = (state: RootState) => state.ui.antiplagiatDocumentsInvalidatedAt;

export default uiSlice.reducer;

